import { Alert, Flex, type MantineSpacing, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  title?: string;
  children: ReactNode;
  action: { label: string; onClick: () => void; loading?: boolean };
  mt?: MantineSpacing;
  mb?: MantineSpacing;
};

/**
 * A warning on a meeting page that asks the adviser to act. The meeting page can stack several, so
 * they share one look. The body is capped to a readable line length, and the action sits beside it,
 * dropping beneath it on a narrow screen rather than squeezing the text.
 */
export function MeetingBanner({ title, children, action, mt, mb }: Props) {
  return (
    <Alert
      variant="light"
      color="yellow"
      radius="md"
      icon={<IconAlertTriangle size={20} />}
      py="sm"
      mt={mt}
      mb={mb}
      styles={{ root: { border: '1px solid var(--mantine-color-yellow-3)' } }}
    >
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        align={{ base: 'flex-start', sm: 'center' }}
        justify="space-between"
        gap={{ base: 'xs', sm: 'md' }}
      >
        <Stack gap="xxs" maw="75ch">
          {title && (
            <Text size="sm" fw={FONT_WEIGHTS.semibold}>
              {title}
            </Text>
          )}
          <Text size="sm">{children}</Text>
        </Stack>
        <Button
          variant="primary"
          size="xs"
          style={{ flexShrink: 0 }}
          onClick={action.onClick}
          loading={action.loading}
        >
          {action.label}
        </Button>
      </Flex>
    </Alert>
  );
}
